import { QRCodeCanvas } from 'qrcode.react';
import React, { useEffect, useState } from 'react';
import { Button, MenuItem, TextField, Typography } from '@material-ui/core';
import apiURL from '../../apiURL';

export const ContestActivation = ({ contestId }: { contestId: string }) => {
    const [activation, setActivation] = useState<any>(null);
    const [events, setEvents] = useState<any[]>([]);
    const [eventId, setEventId] = useState('');
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (!contestId) return;
        const token = localStorage.getItem('auth') || '';
        fetch(`${apiURL}/contest-admin/${contestId}/events`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(async response => {
                if (!response.ok) throw new Error('Liste indisponible');
                return response.json();
            })
            .then(data => setEvents(Array.isArray(data) ? data : []))
            .catch(() => setMessage('Impossible de charger les événements.'));
    }, [contestId]);

    const loadActivation = async () => {
        const token = localStorage.getItem('auth') || '';
        const response = await fetch(
            `${apiURL}/contest-admin/${contestId}/activation`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error('Statut indisponible');
        setActivation(await response.json());
    };

    useEffect(() => {
        if (contestId) loadActivation().catch(() =>
            setMessage('Impossible de charger le statut du concours.')
        );
    }, [contestId]);

    const publicLink = activation?.public_token
        ? `https://karaoke-kreyol.com/concours/${activation.public_token}`
        : '';

    const qrAllowed = () => {
        try {
            const host = window.location.hostname;
            const expected = host === 'admin-test.karaoke-kreyol.com'
                ? 'test.karaoke-kreyol.com'
                : host === 'admin.karaoke-kreyol.com'
                    ? 'karaoke-kreyol.com'
                    : '';
            const url = new URL(publicLink);
            return !!expected &&
                url.protocol === 'https:' &&
                url.hostname === expected &&
                /^\/concours\/[a-f0-9]{48}$/.test(url.pathname) &&
                !url.search && !url.hash;
        } catch {
            return false;
        }
    };
    const downloadQR = () => {
        if (!qrAllowed()) { setMessage('QR code refusé : environnement ou lien incorrect.'); return; }
        const canvas = document.getElementById('contest-qr') as HTMLCanvasElement;
        if (!canvas) return;
        const link = document.createElement('a');
        link.download = 'karaoke-kreyol-qr-concours.png';
        link.href = canvas.toDataURL('image/png');
        link.click();
    };

    const printQR = () => {
        if (!publicLink) return;
        if (!qrAllowed()) { setMessage('QR code refusé : environnement ou lien incorrect.'); return; }
        const canvas = document.getElementById('contest-qr') as HTMLCanvasElement;
        if (!canvas) return;
        const image = canvas.toDataURL('image/png');
        const windowPrint = window.open('', '_blank');
        if (!windowPrint) return;
        windowPrint.document.write(
            '<html><head><title>QR code Jeux concours</title></head>' +
            '<body style="text-align:center;font-family:Arial;padding:40px">' +
            '<h2>Karaoké Kréyòl — Jeux concours</h2>' +
            '<img width="320" height="320" src="' + image + '" />' +
            '</body></html>'
        );
        windowPrint.document.close();
        windowPrint.onload = () => {
            windowPrint.focus();
            windowPrint.print();
        };
    };

    const closeContest = async () => {
        if (busy || !activation?.registrations_open) return;
        if (!window.confirm('Fermer les inscriptions ? Le QR code sera conservé.')) return;
        setBusy(true);
        try {
            const token = localStorage.getItem('auth') || '';
            const r = await fetch(`${apiURL}/contest-admin/${contestId}/close`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!r.ok) throw new Error();
            await loadActivation();
            setMessage('Inscriptions fermées. QR code conservé.');
        } catch {
            setMessage('Impossible de fermer les inscriptions.');
        } finally {
            setBusy(false);
        }
    };

    const activate = async () => {
        if (!eventId || busy) return;
        if (!window.confirm('Ouvrir les inscriptions pour cet événement ?')) return;

        setBusy(true);
        setMessage('');
        try {
            const token = localStorage.getItem('auth') || '';
            const response = await fetch(
                `${apiURL}/contest-admin/${contestId}/activate`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ event_id: eventId }),
                }
            );
            if (!response.ok) throw new Error('Activation impossible');
            await loadActivation();
            setMessage('Concours activé. Les inscriptions sont ouvertes.');
        } catch {
            setMessage('Impossible d’activer ce concours.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div style={{ padding: 20, maxWidth: 640 }}>
            <Typography variant="h6">Activation du concours</Typography>
            <Typography paragraph>
                Choisissez un événement appartenant à l’organisateur.
            </Typography>
            <TextField
                select
                fullWidth
                label="Événement"
                value={eventId}
                onChange={e => setEventId(e.target.value)}
                margin="normal"
            >
                {events.map(event => (
                    <MenuItem key={event.id} value={event.id}>
                        {event.name}
                    </MenuItem>
                ))}
            </TextField>
            <div style={{ marginTop: 20 }}>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={!eventId || busy}
                    onClick={activate}
                >
                    {busy ? 'Activation…' : 'Activer le concours'}
                </Button>
            </div>
            {activation && (
                <div style={{ marginTop: 24 }}>
                    <Typography variant="h6">État du concours</Typography>
                    <Typography>
                        {activation.registrations_open
                            ? 'Inscriptions ouvertes'
                            : 'Inscriptions fermées'}
                    </Typography>
                    {activation.registrations_open && (
                        <Button variant="outlined" color="secondary"
                            disabled={busy} onClick={closeContest}
                            style={{ marginTop: 16 }}>
                            Fermer les inscriptions
                        </Button>
                    )}
                    {activation.public_token && !qrAllowed() && <Typography style={{color:"#b42318",fontWeight:700}}>QR code bloqué : domaine ou lien incorrect.</Typography>}
                    {activation.public_token && qrAllowed() && (
                        <div style={{ marginTop: 24, textAlign: 'center' }}>
                            <Typography variant="h6">QR code permanent</Typography>
                            <div style={{ margin: '20px auto' }}>
                                <QRCodeCanvas
                                    id="contest-qr"
                                    value={publicLink}
                                    size={320}
                                    includeMargin
                                    level="H"
                                    style={{ maxWidth: '100%', height: 'auto' }}
                                />
                            </div>
                            <Button variant="outlined" onClick={downloadQR}>
                                Télécharger PNG
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={printQR}
                                style={{ marginLeft: 12 }}
                            >
                                Imprimer
                            </Button>
                        </div>
                    )}
                    {activation.public_token && qrAllowed() && (
                        <div style={{ marginTop: 16 }}>
                            <Typography>Votre lien permanent :</Typography>
                            <Typography style={{ overflowWrap: 'anywhere' }}>
                                {`${window.location.protocol}//karaoke-kreyol.com/concours/${activation.public_token}`}
                            </Typography>
                            <Button
                                variant="outlined"
                                style={{ marginTop: 12 }}
                                onClick={() => navigator.clipboard.writeText(
                                    `https://karaoke-kreyol.com/concours/${activation.public_token}`
                                )}
                            >
                                Copier le lien
                            </Button>
                        </div>
                    )}
                </div>
            )}
            {message && <Typography style={{ marginTop: 16 }}>{message}</Typography>}
        </div>
    );
};
